import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {AccountLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {HEADER_HEIGHT, MAX_SELECTED_ENTITIES, NO_RESULTS_MESSAGE} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {AccountDownloadTool} from 'src/modules/account-download-tool';
import {getUserInfo} from 'src/openapi/admin/apis/GetUserInfoApi';
import {runAccountIdsSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Cell, DataTable, TableHeader} from '@pb/kit-component-data-table-v2';


import {
    ConferenceEndDateCell,
    DefaultCell,
    LicenseEndDateCell,
    LicenseTypeCell,
    LicensedEnabledUsersCell,
    PrimaryManagerCell,
} from './components/cells';
import {
    AccountNameHeader,
    ConferenceEndsHeader,
    DefaultHeader,
    LicenseEndDateHeader,
    LicenseTypeHeader,
    LicensedEnabledUsersHeader,
    PrimaryManagerHeader,
    SortableHeader,
} from './components/headers';
import {UserConferencesPagination} from './components/pagination';
import {COLUMNS, COLUMNS_DATA, PLACE, SELECTED_COLUMNS, SELECTION_KEY, SORTING_ERROR} from './constants';
import {useGetColumnValue} from './hooks/useGetColumnValue';
import {useGetUserConferences} from './hooks/useGetUserConferences';
import {RemoveConferencesTool} from './remove-conference-tool';
import {userConferencesConferenceActions} from './slices/user-conferences-conference.slice';
import {userConferencesLicenseActions} from './slices/user-conferences-license.slice';
import {userConferencesLicensedUsersActions} from './slices/user-conferences-licensed-users.slice';
import {userConferencesPrimaryManagerActions} from './slices/user-conferences-primary-manager.slice';
import {userConferencesStateActions, userConferencesStateSelector} from './slices/user-conferences-state.slice';
import {userConferencesActions} from './slices/user-conferences.slice';

import type {CellOptions, Column, HeaderCellOptions, RowsParams} from '@pb/kit-component-data-table-v2';
import type {SearchCriteriaType} from 'src/components/criteria-strip/services/criteria-strip-slice';

type Props = {
    userId: number,
    onRefresh: () => Promise<void>,
};

export const UserConferencesController = ({userId, onRefresh}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {hasAccess} = useAdminAccessRight();

    const {loadingError, result, pagination, cacheKeys} = useGetUserConferences();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {getColumnValue} = useGetColumnValue();

    const renderedRows = useMemo(() => {
        if (!result || !Array.isArray(result.data)) {
            return undefined;
        }

        return result.data.map(account => ({
            id: account.accountId,
            columnValues: SELECTED_COLUMNS.reduce((acc, column) => ({
                ...acc,
                [column]: getColumnValue(column, account),
            }), {}),
        }));
    }, [result, getColumnValue]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});
    const rows = useMemo(() => result?.data.map(({accountId}) => accountId) || [], [result]);
    const totalCount = result?.totalCount || 0;

    const getAllEntityIds = useCallback(() => apiDispatch(runAccountIdsSearch({
        accountIdsSearchRequest: {
            searchCriteria: {accountIds: cacheKeys.searchCriteria.accountIds} as SearchCriteriaType,
            sortCriteria: {
                columnId: cacheKeys.sortCriteria.field,
                direction: cacheKeys.sortCriteria.direction,
            },
        },
    })), [cacheKeys, apiDispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userConferencesActions.clearData());
            dispatch(userConferencesStateActions.clearData());
            dispatch(userConferencesLicenseActions.clearData());
            dispatch(userConferencesLicensedUsersActions.clearData());
            dispatch(userConferencesPrimaryManagerActions.clearData());
            dispatch(userConferencesConferenceActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshPage = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userConferencesStateActions.setFilters({}));
        onRefresh();
    }, [onRefresh, dispatch]);

    return (
        <ServerResponseHandler serverError={loadingError?.message === SORTING_ERROR ? null : loadingError}>
            <BoxLoading loading={!result || !cacheKeys.searchCriteria.accountIds}>
                <ToolsRibbonComponent {...{
                    rows: result?.data.map(({accountId}) => accountId) || [],
                    selectionKey: SELECTION_KEY,
                    totalCount: result?.totalCount || 0,
                    extraTools: ids => <AccountDownloadTool accountIds={ids} viewType="Conferences"/>,
                }}>
                    {conferenceIds => hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT) ? (
                        <RemoveConferencesTool
                            userId={userId}
                            conferenceIds={conferenceIds as number[]}
                            onDone={refreshPage}
                        />
                    ) : null}
                </ToolsRibbonComponent>
                <DataTable
                    data={{
                        rows: renderedRows,
                        columns: SELECTED_COLUMNS.map(code => {
                            const column =
                                COLUMNS_DATA.find(({columnId}) => code === columnId) || {
                                    columnId: code,
                                    fixed: false,
                                    defaultWidth: 200,
                                    minWidth: 200,
                                    maxWidth: 400,
                                };

                            return ({
                                ...column,
                                width: getColumnWidthByColumnId(column.columnId),
                            });
                        }),
                    }}
                    getHeaderTopOffset={() => HEADER_HEIGHT}
                    onStopResize={onStopResize}
                    headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    {children}
                                </TableHeader>
                            </Cell>
                        );

                        switch (column.columnId) {
                            case COLUMNS.ACCOUNT_NAME:
                                return (
                                    <Cell
                                        {...cellOptions}
                                        key={`${column.columnId}`}
                                        column={column}
                                        resizable={true}>
                                        <MainColumnHeader
                                            selection={(
                                                <SelectionHeader
                                                    selectionKey={SELECTION_KEY}
                                                    entityIds={rows}
                                                    totalCount={result?.totalCount || 0}
                                                    maxSelected={MAX_SELECTED_ENTITIES}
                                                    getEntityIds={getAllEntityIds}
                                                />
                                            )}
                                            maxRowNumber={maxRowNumber}>
                                            <AccountNameHeader/>
                                        </MainColumnHeader>
                                    </Cell>
                                );
                            case COLUMNS.LICENSED_ENABLED_USERS:
                                return wrapper(<LicensedEnabledUsersHeader/>);
                            case COLUMNS.PRIMARY_ACCOUNT_MANAGER:
                                return wrapper(<PrimaryManagerHeader/>);
                            case COLUMNS.LICENSE_TYPE:
                                return wrapper(<LicenseTypeHeader/>);
                            case COLUMNS.LICENSE_END_DATE:
                                return wrapper(<LicenseEndDateHeader/>);
                            case COLUMNS.CONFERENCE_END_DATE:
                                return wrapper(<ConferenceEndsHeader/>);
                            case COLUMNS.ACCOUNT_STATUS:
                                return wrapper(<SortableHeader columnId={column.columnId}/>);
                            default:
                                return wrapper(<DefaultHeader columnId={column.columnId}/>);
                        }
                    }}
                    renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                        const {rowIndex, column: {columnId}} = rowsParams;
                        const value = rowsParams.row.columnValues[columnId] as string;
                        const rowId = +rowsParams.row.id;
                        const column = rowsParams.column;
                        const cellKey = rowIndex + columnId.toString();
                        const rowNumber = getPaginatedRowNumber({
                            ...pagination,
                            index: rowIndex + 1,
                        });

                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {children}
                            </Cell>
                        );

                        switch (columnId) {
                            case COLUMNS.ACCOUNT_NAME:
                                return (
                                    <MainColumnCell
                                        key={cellKey}
                                        {...{
                                            selection: (
                                                <SelectionRow
                                                    rows={rows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={SELECTION_KEY}
                                                    totalCount={totalCount}
                                                />
                                            ),
                                            rowId,
                                            maxRowNumber,
                                            rowNumber,
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <AccountLink accountId={rowId} place={PLACE} accountName={value}/>
                                    </MainColumnCell>
                                );
                            case COLUMNS.LICENSED_ENABLED_USERS:
                                return wrapper(<LicensedEnabledUsersCell accountId={rowId}/>);
                            case COLUMNS.PRIMARY_ACCOUNT_MANAGER:
                                return wrapper(<PrimaryManagerCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_TYPE:
                                return wrapper(<LicenseTypeCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_END_DATE:
                                return wrapper(<LicenseEndDateCell accountId={rowId}/>);
                            case COLUMNS.CONFERENCE_END_DATE:
                                return wrapper(<ConferenceEndDateCell accountId={rowId}/>);
                            default:
                                return wrapper(<DefaultCell value={value}/>);
                        }
                    }}
                    paginationStripe={(
                        <UserConferencesPagination totalCount={result?.totalCount || 0}/>
                    )}
                    showNoDataMsg={!rows?.length}
                    tableMessage={NO_RESULTS_MESSAGE}
                />
            </BoxLoading>
        </ServerResponseHandler>
    );
};

export const UserConferences = ({userId}: {userId: number}) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {searchCriteria} = useSelector(userConferencesStateSelector);

    const loadUserConferences = useCallback(async () => {
        const userDetailsResponse = await apiDispatch(getUserInfo({userId}));

        if (userDetailsResponse.payload.success) {
            dispatch(userConferencesStateActions.setSearchCriteria({
                accountIds: userDetailsResponse.payload.data.virtualAccounts,
            }));
        }
    }, [userId, apiDispatch, dispatch]);

    useEffect(() => {
        loadUserConferences();
    }, [loadUserConferences]);

    return searchCriteria.accountIds ? (
        <UserConferencesController userId={userId} onRefresh={loadUserConferences}/>
    ) : null;
};
